<template>
  <div class="clientes-simples-page" data-testid="clientes-simples-page">
    <div class="clientes-simples-actions">
      <Button
        :label="t('Novo cliente')"
        icon="pi pi-plus"
        class="clientes-simples-novo-button"
        data-testid="clientes-simples-novo-button"
        @click="handleNovoCliente"
      />
    </div>

    <div
      v-if="isLoading"
      class="clientes-simples-loading"
      data-testid="clientes-simples-loading"
    >
      <progress-bar mode="indeterminate" style="height: 6px" />
    </div>

    <inline-message
      v-else-if="tableData.length === 0"
      severity="info"
      data-testid="clientes-simples-empty"
    >
      {{ t('Nenhum cliente encontrado para o filtro atual.') }}
    </inline-message>

    <data-table
      v-else
      :config="datatableConfig"
      :data="decoratedRows"
      :refresh-button="refreshButtonConfig"
      data-testid="clientes-simples-data-table"
      @edit-click="handleEditClick"
      @refresh="loadData"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useCliente } from '../../composables/useCliente';
import { formatDocumento, formatStatusBadge } from './clientesSimplesFormatters';
import type { Clientes_CarregaClientesRow } from '../../contracts/Clientes_CarregaClientesRow';

type ClienteId = number | string;

interface CellParams {
  value?: unknown;
  data: Clientes_CarregaClientesRow & { _isFrozenFooter?: boolean };
}

const { t } = useI18n();
const { fetchClientesList } = useCliente() as unknown as {
  fetchClientesList: () => Promise<Clientes_CarregaClientesRow[]>;
};

const tableData = ref<Clientes_CarregaClientesRow[]>([]);
const isLoading = ref(true);

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar'),
}));

const franquiaCellRenderer = (params: CellParams): string => {
  if (params.data._isFrozenFooter) {
    return params.value != null ? `<strong>${params.value}</strong>` : '';
  }
  const name = params.value == null ? '' : String(params.value);
  const id = params.data.franquia_id ?? '';
  return `<span data-testid="clientes-simples-row-franquia" data-franquia-id="${id}">${name}</span>`;
};

// A coluna Documento mostra tipo + valor juntos ("CPF: 12345678900"), e o
// backend entrega os dois separados — a junção é de apresentação, então mora
// aqui e não na query.
const decoratedRows = computed(() =>
  tableData.value.map((row) => ({
    ...row,
    _documento_label: formatDocumento(row.documento, row.documento_tipo, t),
  })),
);

const tableColumns = computed(() => [
  {
    data: 'cliente_id',
    title: t('ID'),
    width: 160,
    sortable: true,
    renderType: 'clickable_id',
    dataType: 'number',
    mergedColumns: [
      {
        data: 'franquia',
        title: t('Franquia'),
        cellRenderer: franquiaCellRenderer,
      },
    ],
  },
  {
    data: 'nome',
    title: t('Nome'),
    width: 220,
    sortable: true,
    dataType: 'text',
  },
  {
    data: '_documento_label',
    sortField: 'documento',
    title: t('Documento'),
    width: 180,
    sortable: true,
    dataType: 'text',
  },
  {
    data: 'localidade',
    title: t('Localidade'),
    width: 160,
    sortable: true,
    dataType: 'text',
  },
  {
    data: 'telefone_completo',
    title: t('Contato'),
    width: 220,
    sortable: true,
    dataType: 'text',
    mergedColumns: [
      { data: 'email', title: t('Email') },
    ],
  },
  {
    data: 'data_cadastro',
    title: t('Data cadastro'),
    width: 150,
    sortable: true,
    renderType: 'datetime',
    dataType: 'date',
  },
  {
    data: 'status',
    title: t('Status'),
    width: 120,
    sortable: true,
    renderType: 'badge',
    badgeConfig: (row: Clientes_CarregaClientesRow) => formatStatusBadge(row.status, t),
  },
]);

const datatableConfig = computed(() => ({
  tableId: 'clientesSimplesDataTable',
  title: t('Clientes'),
  filename: `clientes_simples_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  pagination: true,
  pageLength: 50,
  rowTestId: (data: Record<string, unknown>) => {
    const id = data?.cliente_id as number | string | undefined;
    return id == null ? '' : `clientes-simples-row-${id}`;
  },
}));

const loadData = async (): Promise<void> => {
  log('ClientesSimplesPage', 'loadData', 'Fetching clientes list');
  isLoading.value = true;
  try {
    tableData.value = await fetchClientesList();
  } finally {
    isLoading.value = false;
  }
};

const handleNovoCliente = (): void => {
  log('ClientesSimplesPage', 'handleNovoCliente', 'Opening creation modal');
  eventBus.emit('modal.cliente.open', { action: 'new' });
};

const handleEditClick = (id: ClienteId): void => {
  log('ClientesSimplesPage', 'handleEditClick', 'Opening cliente modal', id);
  eventBus.emit('modal.cliente.open', { id });
};

const handleFilterUpdated = (): void => {
  log('ClientesSimplesPage', 'event:filter.updated', 'Received — reloading');
  loadData();
};

const handleClienteSaved = (): void => {
  log('ClientesSimplesPage', 'event:cliente.saved', 'Received — reloading');
  loadData();
};

const handleClienteDeleted = (): void => {
  log('ClientesSimplesPage', 'event:cliente.deleted', 'Received — reloading');
  loadData();
};

onMounted(() => {
  log('ClientesSimplesPage', 'mounted', 'Component mounted');
  loadData();
  eventBus.on('filter.updated', handleFilterUpdated);
  eventBus.on('cliente.saved', handleClienteSaved);
  eventBus.on('cliente.deleted', handleClienteDeleted);
});

onBeforeUnmount(() => {
  log('ClientesSimplesPage', 'beforeUnmount', 'Cleaning up listeners');
  eventBus.off('filter.updated', handleFilterUpdated);
  eventBus.off('cliente.saved', handleClienteSaved);
  eventBus.off('cliente.deleted', handleClienteDeleted);
});
</script>

<style scoped>
.clientes-simples-page {
  padding: 20px;
}

.clientes-simples-actions {
  margin-bottom: 16px;
}

.clientes-simples-loading {
  margin: 16px 0;
}

.clientes-simples-novo-button {
  background: var(--p-primary-color);
  border-color: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
}

.clientes-simples-novo-button:hover {
  background: var(--p-primary-hover-color);
  border-color: var(--p-primary-hover-color);
  color: var(--p-primary-contrast-color);
}
</style>
